/**
 * UndoContext — session-local undo stack (Block 6.B.1 / ADR-017
 * cross-cutting #3 Phase 1 floor).
 *
 * Up to 20 entries; FIFO eviction (Q4 disposition). Each successful
 * save pushes an entry; the UndoToast surfaces only the most-recent
 * entry for 30 seconds (F-O-1: older entries are stack-resident but
 * UI-invisible until PR-3's persistent edit-history surface lands;
 * accepted Phase 1 floor).
 *
 * Per Q8 disposition: provider lives at App.tsx scope (session-scope),
 * NOT inside RouterProvider — route navigation does not drop the stack.
 *
 * The undo callback closes over the API mutation that reverses the
 * change. Callers passing entries are responsible for shaping the
 * reversal correctly (e.g., a PATCH with the `before` value).
 */

import {
  createContext,
  useCallback,
  useContext,
  useReducer,
  type Dispatch,
  type ReactNode,
} from 'react';

const MAX_ENTRIES = 20;

export interface UndoEntry {
  /** Stable id; UI uses for toast keying. */
  id: string;
  /** Human label, e.g. "Phone updated" — surfaces in toast and edit-history. */
  label: string;
  /** Wall-clock when the entry was pushed (ms). */
  pushedAt: number;
  /** Reversal action; rejects on failure (caller surfaces error). */
  undo: () => Promise<void>;
}

interface UndoState {
  entries: UndoEntry[];
}

type UndoAction =
  | { type: 'PUSH'; entry: UndoEntry }
  | { type: 'POP'; id: string }
  | { type: 'CLEAR' };

function reducer(state: UndoState, action: UndoAction): UndoState {
  switch (action.type) {
    case 'PUSH': {
      const next = [...state.entries, action.entry];
      // FIFO eviction at MAX_ENTRIES.
      if (next.length > MAX_ENTRIES) next.shift();
      return { entries: next };
    }
    case 'POP': {
      return { entries: state.entries.filter((e) => e.id !== action.id) };
    }
    case 'CLEAR':
      return { entries: [] };
    default:
      return state;
  }
}

export interface UndoContextValue {
  entries: UndoEntry[];
  /** Push a new entry. Returns the entry id. */
  push: (entry: Omit<UndoEntry, 'id' | 'pushedAt'>) => string;
  /** Invoke the entry's undo callback and remove it from the stack. */
  invoke: (id: string) => Promise<void>;
  /** Remove the entry without invoking (e.g., toast dismissed manually). */
  dismiss: (id: string) => void;
}

export const UndoContext = createContext<UndoContextValue>({
  entries: [],
  push: () => '',
  invoke: async () => {},
  dismiss: () => {},
});

export function UndoProvider({ children }: { children: ReactNode }): ReactNode {
  const [state, dispatch] = useReducer(reducer, { entries: [] });

  const value = useUndoValue(state, dispatch);

  return <UndoContext.Provider value={value}>{children}</UndoContext.Provider>;
}

function useUndoValue(state: UndoState, dispatch: Dispatch<UndoAction>): UndoContextValue {
  const push = useCallback(
    (entry: Omit<UndoEntry, 'id' | 'pushedAt'>): string => {
      const id = `undo-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
      dispatch({
        type: 'PUSH',
        entry: { ...entry, id, pushedAt: Date.now() },
      });
      return id;
    },
    [dispatch],
  );

  const invoke = useCallback(
    async (id: string): Promise<void> => {
      const entry = state.entries.find((e) => e.id === id);
      if (!entry) return;
      try {
        await entry.undo();
      } finally {
        dispatch({ type: 'POP', id });
      }
    },
    [state.entries, dispatch],
  );

  const dismiss = useCallback(
    (id: string): void => {
      dispatch({ type: 'POP', id });
    },
    [dispatch],
  );

  return { entries: state.entries, push, invoke, dismiss };
}

export function useUndo(): UndoContextValue {
  return useContext(UndoContext);
}

/**
 * Exported for tests. Allows asserting reducer behavior without rendering.
 */
export const _internal = { reducer, MAX_ENTRIES };
